import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageMeta from '../../components/base/PageMeta';
import JsonLd from '../../components/base/JsonLd';
import FloatingCtaBar from '../../components/feature/FloatingCtaBar';
import SignUpModal from '../../components/feature/SignUpModal';
import SiteFooter from '../../components/feature/SiteFooter';

export default function HomePage() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  const popularTags = ['Website Builders', 'AI Writing Tools', 'Email Marketing', 'SEO Tools', 'CRM Software'];

  const browseCategories = [
    {
      icon: 'ri-robot-2-line',
      title: 'Software – SaaS – AI',
      desc: 'Online planning, management, and AI-powered tools for creative tasks.',
      count: '155',
      grad: 'from-violet-600 to-purple-700',
      image: 'https://public.readdy.ai/ai/img_res/b390748f2ac0a7aadca98337a6d75526.jpg',
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Finance – Investment',
      desc: 'Personal finance, investment tracking, and wealth management services.',
      count: '89',
      grad: 'from-emerald-500 to-teal-600',
      image: 'https://readdy.ai/api/search-image?query=gold%20coins%20and%20rising%20stock%20market%20graph%20with%20green%20upward%20trend%20arrow%2C%20financial%20growth%20concept%2C%20clean%20white%20background%20with%20emerald%20green%20accents%2C%20sharp%20professional%20photography%2C%20wealth%20and%20investment%20theme%2C%20crisp%20bright%20lighting&width=600&height=300&seq=cat-fin-v2&orientation=landscape',
    },
    {
      icon: 'ri-shopping-cart-2-line',
      title: 'Ecommerce & Shopping',
      desc: 'Online stores, marketplaces, and shopping platforms for every need.',
      count: '126',
      grad: 'from-teal-500 to-cyan-600',
      image: 'https://readdy.ai/api/search-image?query=colorful%20shopping%20bags%20and%20product%20boxes%20arranged%20beautifully%20on%20clean%20white%20surface%2C%20ecommerce%20retail%20concept%2C%20teal%20cyan%20accent%20colors%2C%20sharp%20professional%20product%20photography%2C%20bright%20studio%20lighting%2C%20minimalist%20composition&width=600&height=300&seq=cat-shop-v2&orientation=landscape',
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Education & Learning',
      desc: 'Online courses, learning platforms, and educational resources.',
      count: '94',
      grad: 'from-sky-500 to-indigo-600',
      image: 'https://readdy.ai/api/search-image?query=open%20books%20and%20graduation%20cap%20with%20glowing%20lightbulb%20idea%20concept%2C%20education%20knowledge%20learning%20theme%2C%20clean%20bright%20background%20with%20sky%20blue%20accents%2C%20sharp%20professional%20photography%2C%20inspiring%20academic%20atmosphere%2C%20crisp%20details&width=600&height=300&seq=cat-edu-v2&orientation=landscape',
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Health – Fitness – Wellness',
      desc: 'Fitness tracking software, apps, and health management solutions.',
      count: '62',
      grad: 'from-pink-500 to-rose-600',
      image: 'https://readdy.ai/api/search-image?query=fitness%20dumbbells%20and%20heart%20rate%20pulse%20line%20with%20healthy%20green%20vegetables%20and%20fruits%2C%20wellness%20health%20concept%2C%20pink%20rose%20accent%20colors%2C%20clean%20white%20background%2C%20sharp%20studio%20photography%2C%20vibrant%20fresh%20colors%2C%20energetic%20lifestyle%20theme&width=600&height=300&seq=cat-health-v2&orientation=landscape',
    },
    {
      icon: 'ri-sparkling-2-line',
      title: 'Beauty – Fashion – Lifestyle',
      desc: 'Beauty products, fashion trends, and lifestyle brand resources.',
      count: '47',
      grad: 'from-fuchsia-500 to-pink-500',
      image: 'https://readdy.ai/api/search-image?query=elegant%20luxury%20beauty%20cosmetics%20perfume%20bottle%20and%20fashion%20accessories%20flatlay%20on%20marble%20surface%2C%20fuchsia%20pink%20rose%20gold%20accent%20colors%2C%20premium%20lifestyle%20concept%2C%20sharp%20professional%20product%20photography%2C%20glamorous%20high%20fashion%20aesthetic%2C%20bright%20clean%20lighting&width=600&height=300&seq=cat-beauty-v2&orientation=landscape',
    },
    {
      icon: 'ri-home-smile-line',
      title: 'Home – Garden – DIY',
      desc: 'Home improvement, garden planning, and DIY project resources.',
      count: '75',
      grad: 'from-lime-500 to-green-600',
      image: 'https://readdy.ai/api/search-image?query=cozy%20modern%20living%20room%20interior%20with%20green%20plants%20and%20wooden%20furniture%2C%20home%20decor%20and%20garden%20tools%20concept%2C%20warm%20natural%20light%2C%20lime%20green%20botanical%20accents%2C%20sharp%20professional%20interior%20photography%2C%20inviting%20home%20atmosphere%2C%20clean%20minimalist%20style&width=600&height=300&seq=cat-home-v2&orientation=landscape',
    },
    {
      icon: 'ri-gamepad-line',
      title: 'Gaming – Entertainment',
      desc: 'Online games, streaming platforms, and entertainment content.',
      count: '138',
      grad: 'from-purple-600 to-indigo-700',
      image: 'https://readdy.ai/api/search-image?query=glowing%20RGB%20gaming%20controller%20with%20neon%20purple%20and%20blue%20light%20effects%20on%20dark%20background%2C%20esports%20entertainment%20concept%2C%20vibrant%20neon%20colors%20bokeh%2C%20sharp%20professional%20product%20photography%2C%20high%20energy%20gaming%20atmosphere%2C%20vivid%20dramatic%20lighting&width=600&height=300&seq=cat-game-v2&orientation=landscape',
    },
  ];

  const featuredReviews = [
    {
      name: 'Magai',
      category: 'AI Tools',
      categoryColor: 'text-teal-600 bg-teal-50',
      desc: 'All-in-one AI platform that combines multiple AI models including GPT-4, and health management solutions.',
      rating: '8.9',
      image: 'https://readdy.ai/api/search-image?query=Magai%20AI%20platform%20dashboard%20interface%20screenshot%20showing%20chat%20interface%20and%20AI%20tools%20panel%2C%20clean%20modern%20dark%20UI%20with%20sidebar%20navigation%2C%20professional%20software%20screenshot%20on%20light%20background&width=400&height=220&seq=magai1&orientation=landscape',
    },
    {
      name: 'InVideo',
      category: 'AI Tools',
      categoryColor: 'text-teal-600 bg-teal-50',
      desc: 'AI-powered video creation platform that transforms text into professional video content automatically.',
      rating: '10',
      image: 'https://readdy.ai/api/search-image?query=InVideo%20video%20editor%20dashboard%20screenshot%20showing%20timeline%20interface%20and%20video%20editing%20tools%2C%20dark%20modern%20UI%20with%20colorful%20thumbnails%20and%20video%20clips%20panel%2C%20professional%20video%20software%20screenshot&width=400&height=220&seq=invideo1&orientation=landscape',
    },
    {
      name: 'ElevenLabs',
      category: 'AI Tools',
      categoryColor: 'text-teal-600 bg-teal-50',
      desc: 'Leading AI voice synthesis platform offering realistic text-to-speech and voice cloning technology.',
      rating: '7.1',
      image: 'https://readdy.ai/api/search-image?query=ElevenLabs%20AI%20voice%20synthesis%20platform%20dashboard%20with%20audio%20waveform%20visualization%2C%20clean%20modern%20UI%20showing%20voice%20cloning%20interface%20with%20purple%20blue%20accent%20colors%2C%20professional%20audio%20software%20screenshot%20on%20light%20background&width=400&height=220&seq=elevenlabs1&orientation=landscape',
    },
    {
      name: 'Skylum',
      category: 'Design Tools',
      categoryColor: 'text-orange-600 bg-orange-50',
      desc: 'AI-powered photo editing software featuring Luminar Neo with advanced AI sky replacement tools.',
      rating: '5.9',
      image: 'https://readdy.ai/api/search-image?query=Skylum%20Luminar%20photo%20editing%20software%20dashboard%20showing%20AI%20photo%20enhancement%20interface%2C%20nature%20landscape%20photo%20being%20edited%20with%20sky%20replacement%2C%20modern%20dark%20UI%20design%20with%20editing%20toolbar%2C%20professional%20photography%20software%20screenshot%20with%20typography%20tools&width=400&height=220&seq=skylum1&orientation=landscape',
    },
    {
      name: 'Akool',
      category: 'AI Tools',
      categoryColor: 'text-teal-600 bg-teal-50',
      desc: 'Generative AI platform for creating personalized marketing content with face swap and video capabilities.',
      rating: '9.1',
      image: 'https://readdy.ai/api/search-image?query=Akool%20AI%20generative%20content%20platform%20dashboard%20showing%20face%20swap%20and%20marketing%20video%20creation%20interface%2C%20modern%20clean%20UI%20with%20campaign%20management%20panel%2C%20colorful%20marketing%20content%20examples%20on%20light%20background&width=400&height=220&seq=akool1&orientation=landscape',
    },
    {
      name: 'Webflow',
      category: 'Website Builders',
      categoryColor: 'text-blue-600 bg-blue-50',
      desc: 'Professional website builder with advanced design capabilities and CMS for developers and designers.',
      rating: '9.2',
      image: 'https://readdy.ai/api/search-image?query=Webflow%20website%20builder%20dashboard%20showing%20visual%20design%20interface%20with%20canvas%20and%20element%20panel%2C%20clean%20modern%20UI%20with%20white%20background%20and%20blue%20accent%20colors%2C%20professional%20web%20design%20software%20screenshot%20with%20typography%20tools&width=400&height=220&seq=webflow1&orientation=landscape',
    },
  ];

  const topComparisons = [
    {
      a: { name: 'Wix', icon: 'ri-layout-4-line', color: 'bg-blue-100 text-blue-600' },
      b: { name: 'WordPress', icon: 'ri-global-line', color: 'bg-gray-800 text-white' },
      criteria: [
        { label: 'Ease of Use', a: true, b: false },
        { label: 'Customization', a: false, b: true },
        { label: 'Pricing', a: true, b: true },
      ],
    },
    {
      a: { name: 'Shopify', icon: 'ri-store-2-line', color: 'bg-green-100 text-green-700' },
      b: { name: 'WooCommerce', icon: 'ri-shopping-bag-line', color: 'bg-purple-100 text-purple-600' },
      criteria: [
        { label: 'Setup Speed', a: true, b: false },
        { label: 'Cost Control', a: false, b: true },
        { label: 'Support', a: true, b: false },
      ],
    },
    {
      a: { name: 'Notion', icon: 'ri-file-text-line', color: 'bg-gray-100 text-gray-800' },
      b: { name: 'ClickUp', icon: 'ri-checkbox-multiple-line', color: 'bg-pink-100 text-pink-600' },
      criteria: [
        { label: 'Flexibility', a: true, b: false },
        { label: 'Features', a: false, b: true },
        { label: 'Learning Curve', a: true, b: false },
      ],
    },
    {
      a: { name: 'Figma', icon: 'ri-pen-nib-line', color: 'bg-orange-100 text-orange-600' },
      b: { name: 'Sketch', icon: 'ri-artboard-line', color: 'bg-yellow-100 text-yellow-700' },
      criteria: [
        { label: 'Collaboration', a: true, b: false },
        { label: 'Performance', a: false, b: true },
        { label: 'Cross-platform', a: true, b: false },
      ],
    },
  ];

  const steps = [
    { num: '01', icon: 'ri-search-eye-line', title: 'In-depth Research', desc: 'Our team of experts thoroughly researches each platform on features, pricing, policies, and real-world experience.' },
    { num: '02', icon: 'ri-flask-line', title: 'Real-world Testing', desc: 'We directly test every tool, evaluate performance, and assess the quality of customer support.' },
    { num: '03', icon: 'ri-scales-3-line', title: 'Objective Analysis', desc: 'Comparisons based on clear criteria: value, features, reliability, and fit for different types of users.' },
    { num: '04', icon: 'ri-refresh-line', title: 'Continuous Updates', desc: 'We monitor and update information whenever significant changes occur on the platforms we review.' },
  ];

  const testimonials = [
    { name: 'Nguyen Minh Tuan', role: 'Digital Marketer', content: 'Compare247 saves me hours of research. Clear, non-exaggerated information — very useful when choosing marketing tools.', avatar: 'N' },
    { name: 'Tran Thi Huong', role: 'Freelancer & Content Creator', content: 'I appreciate the neutrality of Compare247. No upselling, just providing information so I can decide for myself.', avatar: 'T' },
    { name: 'Le Van Duc', role: 'Trader', content: 'The prop trading firm comparison section is very detailed and objective. It helped me avoid unreliable platforms.', avatar: 'L' },
  ];

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        name: '247digitrend',
        url: siteUrl,
        description: 'Unbiased digital tool reviews and comparisons of AI, SaaS, and marketing software',
        potentialAction: {
          '@type': 'SearchAction',
          target: `${siteUrl}/?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        name: '247digitrend',
        url: siteUrl,
        sameAs: [
          'https://twitter.com/247digitrend',
          'https://facebook.com/247digitrend',
          'https://linkedin.com/company/247digitrend',
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: 'hello@247digitrend.com',
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${siteUrl}/#webpage`,
        url: siteUrl,
        name: 'Compare247 – Compare & Review Digital Tools 24/7',
        description:
          'Unbiased digital tool reviews, SaaS comparisons, and AI software ratings. Helping 50K+ readers choose the best tools for their business.',
        isPartOf: { '@id': `${siteUrl}/#website` },
        about: { '@id': `${siteUrl}/#organization` },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          ],
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title="Compare247 – Compare & Review Digital Tools | Unbiased SaaS & AI Reviews"
        description="Compare247 offers unbiased reviews and side-by-side comparisons of AI tools, SaaS platforms, and digital software. 500+ tools reviewed. Helping 50K+ readers decide smarter."
        canonical={`${siteUrl}/`}
        keywords="compare digital tools, AI tools review, SaaS comparison, software review, compare247"
      />
      <JsonLd data={websiteSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ── HEADER ── */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-white'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Home</a>
            <a href="/categories" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Categories</a>
            <a href="/reviews" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Reviews</a>
            <a href="/comparisons" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Comparisons</a>
            <a href="/blog" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Blog</a>
          </nav>
          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full px-3 py-1.5 cursor-pointer"
              >
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map((lang) => (
                    <button
                      key={lang}
                      onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${currentLang === lang ? 'bg-violet-50 text-violet-700' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span>
                      <span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Sign Up Button */}
            <button
              onClick={() => setShowSignUp(true)}
              className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
            >
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="pt-44 pb-16" style={{ background: 'linear-gradient(160deg, #faf5ff 0%, #fdf2ff 40%, #fff0fb 100%)' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-[72px] leading-[1.1] font-black text-gray-900 mb-2 tracking-tight">
            Compare &amp; Review
          </h1>
          <h2 className="text-[72px] leading-[1.15] font-black tracking-tight mb-6"
            style={{ background: 'linear-gradient(90deg, #9333ea, #c026d3, #e879f9)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            Digital Tools
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-2xl mx-auto">
            We provide unbiased, real-world reviews to help you choose the best digital tools for your business. No hype, just honest information.
          </p>
          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-5 py-2.5 mb-8 shadow-sm">
            <i className="ri-star-fill text-violet-500 text-base"></i>
            <span className="text-sm font-semibold text-gray-800">500+ Tools Reviewed</span>
          </div>
          <div className="flex items-center max-w-xl mx-auto bg-white rounded-full border border-gray-200 shadow-sm overflow-hidden mb-6 px-1">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && searchQuery.trim()) navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`); }}
              placeholder="Search tools, reviews, comparisons..."
              className="flex-1 px-5 py-3.5 text-sm text-gray-700 placeholder-gray-400 bg-transparent outline-none"
            />
            <button
              onClick={() => { if (searchQuery.trim()) navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`); }}
              className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-6 py-3 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity m-0.5">
              Search
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2.5">
            <span className="text-sm font-semibold text-gray-700">Popular:</span>
            {popularTags.map((tag, i) => (
              <button key={i} className="text-sm font-medium text-violet-600 border border-violet-300 rounded-full px-4 py-1.5 hover:bg-violet-50 transition-colors cursor-pointer whitespace-nowrap">
                {tag}
              </button>
            ))}
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-6 mt-14">
          <div className="grid grid-cols-4 gap-8 text-center">
            {[
              { num: '500+', label: 'Tools Reviewed' },
              { num: '150+', label: 'Comparisons' },
              { num: '50K+', label: 'Monthly Readers' },
              { num: '100%', label: 'Unbiased Reviews' },
            ].map((stat, i) => (
              <div key={i}>
                <div className="text-4xl font-black mb-1"
                  style={{ background: 'linear-gradient(90deg, #9333ea, #c026d3)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  {stat.num}
                </div>
                <div className="text-sm text-gray-500 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BROWSE BY CATEGORY ── */}
      <section id="categories" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-black text-gray-900 mb-2">Browse by Category</h3>
            <p className="text-sm text-gray-500">Explore tools organized by category to find exactly what you need for your business</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {browseCategories.map((cat, i) => (
              <a key={i} href="/categories" className="group relative flex flex-col bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-violet-200 hover:shadow-md transition-all cursor-pointer">
                {/* Image */}
                <div className="relative w-full h-28 overflow-hidden flex-shrink-0">
                  <img
                    src={cat.image}
                    alt={cat.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-br ${cat.grad} opacity-60`}></div>
                  {/* Icon over image */}
                  <div className="absolute bottom-2 left-3 w-9 h-9 flex items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm border border-white/30">
                    <i className={`${cat.icon} text-white text-base`}></i>
                  </div>
                  {/* Count badge */}
                  <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full px-2 py-0.5">
                    <span className="text-xs font-bold text-gray-700">{cat.count}</span>
                  </div>
                </div>
                {/* Text */}
                <div className="p-3">
                  <h4 className="text-sm font-bold text-gray-900 mb-1 leading-tight">{cat.title}</h4>
                  <p className="text-xs text-gray-500 leading-relaxed mb-2 line-clamp-2">{cat.desc}</p>
                  <span className="text-xs font-semibold text-violet-600 inline-flex items-center gap-1">
                    Explore <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </div>
              </a>
            ))}
          </div>

          <div className="text-center">
            <a href="/categories" className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-7 py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">
              View All Categories
            </a>
          </div>
        </div>
      </section>

      {/* ── FEATURED REVIEWS ── */}
      <section id="reviews" className="py-20" style={{ background: '#faf5ff' }}>
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-black text-gray-900 mb-2">Featured Reviews</h3>
            <p className="text-sm text-gray-500">In-depth analysis of the most popular tools in the market</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
            {featuredReviews.map((item, i) => (
              <a key={i} href="/reviews" className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-violet-200 hover:shadow-lg transition-all cursor-pointer block">
                {/* Card image */}
                <div className="relative w-full h-40 overflow-hidden">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  {/* Rating badge */}
                  <div className="absolute top-2 right-2 flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-full px-2 py-0.5 shadow-sm">
                    <i className="ri-star-fill text-yellow-400 text-xs"></i>
                    <span className="text-xs font-bold text-gray-800">{item.rating}</span>
                  </div>
                  {/* Bookmark */}
                  <button className="absolute top-2 left-2 w-7 h-7 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full shadow-sm cursor-pointer hover:bg-violet-50 transition-colors">
                    <i className="ri-bookmark-line text-gray-500 text-xs"></i>
                  </button>
                </div>
                {/* Card body */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-1.5">
                    <h4 className="text-base font-black text-gray-900">{item.name}</h4>
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap ml-2 ${item.categoryColor}`}>{item.category}</span>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed mb-3 line-clamp-2">{item.desc}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-yellow-400 text-xs"></i>
                      <span className="text-xs font-bold text-gray-700">{item.rating}/10</span>
                    </div>
                    <span className="text-xs font-semibold text-violet-600 inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                      Read More <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>

          <div className="text-center">
            <a href="/reviews" className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-7 py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">
              View All Reviews
            </a>
          </div>
        </div>
      </section>

      {/* ── TOP COMPARISONS ── */}
      <section id="compare" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-black text-gray-900 mb-2">Top Comparisons</h3>
            <p className="text-sm text-gray-500">Side-by-side analysis to help you make the right choice</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            {topComparisons.map((comp, i) => (
              <a key={i} href="/comparisons" className="group bg-white rounded-2xl p-5 border border-gray-100 hover:border-violet-200 hover:shadow-md transition-all cursor-pointer block">
                {/* VS header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${comp.a.color} text-lg`}>
                      <i className={comp.a.icon}></i>
                    </div>
                    <span className="text-sm font-bold text-gray-900">{comp.a.name}</span>
                  </div>
                  <span className="text-xs font-black text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">VS</span>
                  <div className="flex items-center gap-2">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${comp.b.color} text-lg`}>
                      <i className={comp.b.icon}></i>
                    </div>
                    <span className="text-sm font-bold text-gray-900">{comp.b.name}</span>
                  </div>
                </div>
                {/* Criteria */}
                <div className="mb-4">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Quick Comparison:</span>
                  <div className="mt-2 space-y-1.5">
                    {comp.criteria.map((c, ci) => (
                      <div key={ci} className="flex items-center justify-between">
                        <span className="text-xs text-gray-600">{c.label}</span>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${c.a ? 'bg-emerald-400' : 'bg-yellow-400'}`}></div>
                          <div className={`w-2 h-2 rounded-full ${c.b ? 'bg-emerald-400' : 'bg-yellow-400'}`}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <span className="text-xs font-semibold text-violet-600 inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                  View Comparison <i className="ri-arrow-right-line"></i>
                </span>
              </a>
            ))}
          </div>

          <div className="text-center">
            <a href="/comparisons" className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-7 py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">
              View All Comparisons
            </a>
          </div>
        </div>
      </section>

      {/* ── LATEST ARTICLES ── */}
      <section id="blog" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-black text-gray-900 mb-2">Latest Articles</h3>
            <p className="text-sm font-medium" style={{ color: '#0d9488' }}>Tips, guides, and insights from our experts</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
            {[
              {
                img: 'https://readdy.ai/api/search-image?query=laptop%20screen%20showing%20modern%20website%20builder%20drag%20and%20drop%20interface%20with%20clean%20UI%20design%20elements%20and%20colorful%20components%20on%20white%20background%2C%20professional%20product%20screenshot%20style%2C%20top%20view%20flat%20lay&width=400&height=220&seq=art01&orientation=landscape',
                tags: [{ label: 'Guides', color: 'text-gray-600 bg-gray-100' }],
                read: '6 min read',
                title: 'How to Choose the Right Website Builder for Your Business',
                desc: 'A comprehensive guide to selecting the perfect website builder based on your specific needs and budget.',
                author: 'Sarah Johnson', date: 'May 18, 2024', avatar: 'S',
              },
              {
                img: 'https://readdy.ai/api/search-image?query=InVideo%20AI%20video%20creation%20platform%20interface%20dark%20theme%20with%20colorful%20video%20timeline%20and%20media%20library%20panel%2C%20professional%20software%20dashboard%20screenshot%20with%20vibrant%20gradient%20thumbnails&width=400&height=220&seq=art02&orientation=landscape',
                tags: [{ label: 'Software', color: 'text-teal-600 bg-teal-50' }, { label: 'AI', color: 'text-teal-600 bg-teal-50' }],
                read: '8 min read',
                title: 'InVideo AI: Transform Your Video Creation Workflow',
                desc: 'Discover how InVideo is revolutionizing video production with text-to-video AI features.',
                author: 'Michael Chen', date: 'May 18, 2024', avatar: 'M',
              },
              {
                img: 'https://readdy.ai/api/search-image?query=ElevenLabs%20AI%20voice%20synthesis%20platform%20with%20audio%20waveform%20visualization%20on%20dark%20background%2C%20futuristic%20sound%20wave%20visualization%20with%20teal%20and%20cyan%20colors%2C%20professional%20audio%20software%20screenshot&width=400&height=220&seq=art03&orientation=landscape',
                tags: [{ label: 'Software', color: 'text-teal-600 bg-teal-50' }, { label: 'SaaS', color: 'text-teal-600 bg-teal-50' }],
                read: '9 min read',
                title: 'ElevenLabs Voice AI: The Future of Text-to-Speech Technology',
                desc: 'Explore ElevenLabs cutting-edge voice synthesis technology that creates incredibly realistic voices.',
                author: 'Emily Rodriguez', date: 'May 18, 2024', avatar: 'E',
              },
              {
                img: 'https://readdy.ai/api/search-image?query=Skylum%20Luminar%20AI%20photo%20editing%20software%20dashboard%20showing%20dramatic%20landscape%20photo%20with%20sky%20replacement%20feature%2C%20dark%20UI%20with%20editing%20sliders%20and%20tools%20panel%2C%20professional%20photography%20software%20screenshot&width=400&height=220&seq=art04&orientation=landscape',
                tags: [{ label: 'Software', color: 'text-teal-600 bg-teal-50' }, { label: 'SaaS', color: 'text-teal-600 bg-teal-50' }],
                read: '12 min read',
                title: 'Skylum AI Photo Editing: Professional Results Made Easy',
                desc: 'Learn how Skylum AI-powered photo editing software featuring Luminar Neo with advanced AI enhances machine learning.',
                author: 'David Park', date: 'May 18, 2024', avatar: 'D',
              },
              {
                img: 'https://readdy.ai/api/search-image?query=Magai%20AI%20content%20creation%20platform%20dashboard%20with%20multiple%20AI%20models%20chat%20interface%2C%20clean%20modern%20UI%20showing%20content%20creation%20tools%20and%20model%20selection%20panel%2C%20light%20background%20with%20purple%20accents%2C%20professional%20SaaS%20screenshot&width=400&height=220&seq=art05&orientation=landscape',
                tags: [{ label: 'Software', color: 'text-teal-600 bg-teal-50' }, { label: 'AI', color: 'text-teal-600 bg-teal-50' }],
                read: '8 min read',
                title: 'Magai: Your All-in-One AI Content Creation Hub',
                desc: 'Discover how Magai comprehensive AI platform combines multiple AI models including GPT-4 and Claude.',
                author: 'Lisa Thompson', date: 'May 18, 2024', avatar: 'L',
              },
              {
                img: 'https://readdy.ai/api/search-image?query=AI%20writing%20tools%20comparison%20chart%20showing%20ChatGPT%20Claude%20Gemini%20side%20by%20side%20feature%20comparison%20table%20on%20clean%20white%20background%2C%20infographic%20style%20with%20purple%20blue%20color%20scheme%2C%20professional%20blog%20article%20header%20image&width=400&height=220&seq=art06&orientation=landscape',
                tags: [{ label: 'Comparisons', color: 'text-violet-600 bg-violet-50' }],
                read: '15 min read',
                title: 'AI Writing Tools Comparison: ChatGPT vs Claude vs Gemini',
                desc: 'We tested the top AI writing assistants to help you find the best tool for content creation.',
                author: 'Michael Chen', date: 'May 18, 2024', avatar: 'M',
              },
              {
                img: 'https://readdy.ai/api/search-image?query=email%20marketing%20campaign%20dashboard%20showing%20open%20rates%20click%20rates%20analytics%20charts%2C%20professional%20email%20marketing%20software%20interface%20with%20orange%20and%20green%20metrics%2C%20clean%20modern%20design&width=400&height=220&seq=art07&orientation=landscape',
                tags: [{ label: 'Marketing', color: 'text-orange-600 bg-orange-50' }],
                read: '10 min read',
                title: 'Email Marketing Best Practices: Boost Your Open Rates in 2026',
                desc: 'Proven strategies and tactics to improve your email marketing campaigns and engagement rates.',
                author: 'Emily Rodriguez', date: 'May 18, 2024', avatar: 'E',
              },
              {
                img: 'https://readdy.ai/api/search-image?query=SEO%20tools%20dashboard%20showing%20keyword%20rankings%20organic%20traffic%20analytics%20charts%20on%20laptop%20screen%2C%20professional%20digital%20marketing%20software%20interface%20with%20green%20and%20blue%20colors%2C%20clean%20modern%20design&width=400&height=220&seq=art08&orientation=landscape',
                tags: [{ label: 'SEO', color: 'text-green-700 bg-green-50' }],
                read: '14 min read',
                title: 'The Ultimate Guide to SEO Tools for Small Businesses',
                desc: 'Discover the most effective and affordable SEO tools to improve your search engine rankings.',
                author: 'David Park', date: 'May 18, 2024', avatar: 'D',
              },
              {
                img: 'https://readdy.ai/api/search-image?query=cloud%20hosting%20vs%20traditional%20hosting%20concept%20visualization%20with%20cloud%20servers%20and%20physical%20server%20racks%2C%20clean%20infographic%20style%20illustration%20with%20blue%20and%20white%20colors%2C%20professional%20tech%20blog%20header%20image&width=400&height=220&seq=art09&orientation=landscape',
                tags: [{ label: 'Hosting', color: 'text-sky-600 bg-sky-50' }],
                read: '8 min read',
                title: 'Cloud Hosting vs Traditional Hosting: Which Is Right for You?',
                desc: 'Understanding the difference between cloud and traditional hosting to make the right decision.',
                author: 'Lisa Thompson', date: 'May 18, 2024', avatar: 'L',
              },
            ].map((article, i) => (
              <a key={i} href="/blog" className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-violet-200 hover:shadow-md transition-all cursor-pointer block">
                {/* Image */}
                <div className="w-full h-36 overflow-hidden">
                  <img src={article.img} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                </div>
                {/* Body */}
                <div className="p-4">
                  {/* Tags + read time */}
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    {article.tags.map((tag, ti) => (
                      <span key={ti} className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tag.color}`}>{tag.label}</span>
                    ))}
                    <span className="text-xs text-gray-400 ml-auto whitespace-nowrap">{article.read}</span>
                  </div>
                  {/* Title */}
                  <h4 className="text-sm font-bold text-gray-900 mb-1.5 leading-snug line-clamp-2 group-hover:text-violet-700 transition-colors">{article.title}</h4>
                  {/* Desc */}
                  <p className="text-xs text-gray-500 leading-relaxed mb-3 line-clamp-2">{article.desc}</p>
                  {/* Author row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-500 text-white text-xs font-bold flex-shrink-0">
                        {article.avatar}
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-700">{article.author}</span>
                        <span className="text-xs text-gray-400 ml-1.5">{article.date}</span>
                      </div>
                    </div>
                    <i className="ri-arrow-right-line text-gray-400 group-hover:text-violet-600 group-hover:translate-x-0.5 transition-all text-base"></i>
                  </div>
                </div>
              </a>
            ))}
          </div>

          <div className="text-center">
            <a href="/blog" className="inline-flex items-center gap-2 text-white text-sm font-semibold px-8 py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
              style={{ background: 'linear-gradient(90deg, #0d9488, #06b6d4)' }}>
              View All Articles
            </a>
          </div>
        </div>
      </section>

      {/* ── NEWSLETTER / CTA ── */}
      <section className="relative py-24 overflow-hidden" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 25%, #c026d3 60%, #ec4899 100%)' }}>
        <div className="max-w-2xl mx-auto px-6 flex items-center justify-center">
          <div className="w-full rounded-2xl border border-white/20 bg-white/10 backdrop-blur-md px-10 py-12 text-center text-white"
            style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
            {/* Icon */}
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-6 rounded-xl border border-white/30 bg-white/10">
              <i className="ri-mail-line text-2xl text-white"></i>
            </div>
            {/* Heading */}
            <h3 className="text-2xl font-black mb-3 text-white">Stay Updated with Latest Reviews</h3>
            <p className="text-sm text-white/80 leading-relaxed mb-7">
              Get weekly insights, tool comparisons, and exclusive tips delivered straight to your inbox. Join 10,000+ subscribers.
            </p>
            {subscribeSuccess ? (
              <div className="flex flex-col items-center gap-3 py-2">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20 border border-white/30">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <p className="text-white font-semibold text-sm">Subscribed successfully! Thank you for joining.</p>
              </div>
            ) : (
              <form
                data-readdy-form
                action="https://readdy.ai/api/form/d6uvnle7ipaugi04lf8g"
                method="POST"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
                  fetch('https://readdy.ai/api/form/d6uvnle7ipaugi04lf8g', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: data.toString(),
                  }).then(() => {
                    form.reset();
                    setSubscribeSuccess(true);
                  });
                }}
                className="flex items-center gap-2 max-w-md mx-auto mb-4"
              >
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="Enter your email address"
                  className="flex-1 px-5 py-3 rounded-full text-sm text-gray-800 placeholder-gray-400 bg-white/90 outline-none border-none"
                />
                <button
                  type="submit"
                  className="bg-white text-violet-700 font-bold text-sm px-6 py-3 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
                >
                  Subscribe Now
                </button>
              </form>
            )}
            <p className="text-xs text-white/60 mt-3">No spam, unsubscribe anytime. We respect your privacy.</p>
          </div>
        </div>
      </section>

      {/* ── FLOATING CTA BAR ── */}
      <FloatingCtaBar onSignUp={() => setShowSignUp(true)} />

      {/* ── FOOTER ── */}
      <SiteFooter />

      <SignUpModal isOpen={showSignUp} onClose={() => setShowSignUp(false)} />
    </div>
  );
}
